import { useState } from "react";
import { Loader2, Menu, MessageCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";

import { LanguageSwitcher } from "@/components/shared/LanguageSwitcher";
import { ThemeSwitcher } from "@/components/shared/ThemeSwitcher";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useSidebar } from "@/contexts/SidebarContext";
import { sendFeedback } from "@/services/authService";

export function Header(): JSX.Element {
  const { toggleSidebar } = useSidebar();
  const { t } = useTranslation();
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState("");
  const [feedbackSending, setFeedbackSending] = useState(false);

  const handleSendFeedback = async (): Promise<void> => {
    const trimmed = feedbackMessage.trim();
    if (!trimmed || feedbackSending) return;
    setFeedbackSending(true);
    try {
      await sendFeedback(trimmed);
      toast.success(t("header.feedbackSuccess"));
      setFeedbackMessage("");
      setFeedbackOpen(false);
    } catch {
      toast.error(t("header.feedbackError"));
    } finally {
      setFeedbackSending(false);
    }
  };

  return (
    <header className="flex h-14 shrink-0 items-center justify-between border-b bg-background px-4 md:h-16 md:px-6">
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          className="lg:hidden"
          onClick={toggleSidebar}
          aria-label={t("header.toggleNavigation")}
        >
          <Menu className="h-5 w-5" />
        </Button>
      </div>
      <div className="flex items-center gap-3">
        <Popover open={feedbackOpen} onOpenChange={setFeedbackOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="gap-2 transition-colors duration-150"
              aria-label={t("header.feedback")}
            >
              <MessageCircle className="h-4 w-4" />
              <span className="hidden sm:inline">{t("header.feedback")}</span>
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-96" align="end">
            <div className="space-y-3">
              <textarea
                className="flex min-h-[100px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                placeholder={t("header.feedbackPlaceholder")}
                value={feedbackMessage}
                onChange={(e) => setFeedbackMessage(e.target.value)}
                maxLength={2000}
                disabled={feedbackSending}
              />
              <div className="flex justify-end">
                <Button
                  type="button"
                  size="sm"
                  onClick={() => void handleSendFeedback()}
                  disabled={!feedbackMessage.trim() || feedbackSending}
                  className="transition-colors duration-150"
                >
                  {feedbackSending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {t("header.feedbackSending")}
                    </>
                  ) : (
                    t("header.feedbackSend")
                  )}
                </Button>
              </div>
            </div>
          </PopoverContent>
        </Popover>
        <ThemeSwitcher />
        <LanguageSwitcher />
      </div>
    </header>
  );
}
